import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DeliverableImportTypes } from '@mono/proto-clients';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useChatFeedback } from '../../ChatFeedbackContext';
import { deliverableImportFeedback } from './card';
import { useDeliverableImportClient } from './client';
import { workstreamLabel } from './shared';
import { invalidateDeliverableImportReview } from './useReview';
const { DeliverableImportAction, DeliverableImportDecision, DeliverableImportVerdict } = DeliverableImportTypes;
/** The canonical field-table rows shown on a proposal review, in display order. */ const FIELD_ROWS = [
    {
        key: 'deliverableNumber',
        label: 'Deliverable Number'
    },
    {
        key: 'title',
        label: 'Title'
    },
    {
        key: 'periodLabel',
        label: 'Period'
    },
    {
        key: 'firstDueDate',
        label: 'Due Date'
    },
    {
        key: 'frequency',
        label: 'Cadence'
    },
    {
        key: 'pwsSections',
        label: 'PWS Section'
    },
    {
        key: 'responsiblePerson',
        label: 'Responsible Person'
    }
];
function formatValue(value) {
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : 'Not provided';
    }
    return value ? value : 'Not provided';
}
/** Every DeliverableFields field worth showing in a revision diff, with a friendly label. */ const FULL_FIELD_LABELS = [
    {
        key: 'deliverableNumber',
        label: 'Deliverable Number'
    },
    {
        key: 'title',
        label: 'Title'
    },
    {
        key: 'periodLabel',
        label: 'Period'
    },
    {
        key: 'firstDueDate',
        label: 'Due Date'
    },
    {
        key: 'seriesEndDate',
        label: 'Series End Date'
    },
    {
        key: 'frequency',
        label: 'Cadence'
    },
    {
        key: 'pwsSections',
        label: 'PWS Section'
    },
    {
        key: 'responsiblePerson',
        label: 'Responsible Person'
    },
    {
        key: 'clinNumber',
        label: 'CLIN'
    },
    {
        key: 'dueDateNotes',
        label: 'Due Date Notes'
    },
    {
        key: 'occurrenceDates',
        label: 'Occurrence Dates'
    }
];
function fieldValuesEqual(a, b) {
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i)=>v === b[i]);
    }
    return a === b;
}
/**
 * Diffs every field between a superseded proposal's values and its
 * revision's — independent of `changed_fields` on the proto, which tracks
 * proposed-vs-existing-task differences, not proposed-vs-previous-revision.
 */ export function diffDeliverableFields(previous, revised) {
    return FULL_FIELD_LABELS.filter(({ key })=>!fieldValuesEqual(previous[key], revised[key])).map(({ key, label })=>({
            key,
            label,
            previous: formatValue(previous[key]),
            revised: formatValue(revised[key])
        }));
}
/**
 * "Create Deliverable" / "Update Deliverable" etc, from
 * DeliverableImportAction. ALREADY_MATCHES and NEEDS_CLARIFICATION get their
 * own plain-language labels since neither is a create-or-update verb.
 */ export function actionLabel(action) {
    switch(action){
        case DeliverableImportAction.CREATE:
            return 'Create Deliverable';
        case DeliverableImportAction.UPDATE:
            return 'Update Deliverable';
        case DeliverableImportAction.ALREADY_MATCHES:
            return 'Already Matches — Nothing Will Change';
        case DeliverableImportAction.NEEDS_CLARIFICATION:
            return 'Needs Clarification';
        default:
            return 'Unspecified Action';
    }
}
/**
 * Field table over the canonical 8 rows (Deliverable Number, Workstream,
 * Title, Period, Due Date, Cadence, PWS Section, Responsible Person). An
 * empty value always renders "Not provided". For UPDATE, a row in
 * `changedFields` splits into Existing / Proposed columns; every other row
 * (and every row when there's nothing to compare against) is a single value.
 * Shared by PROPOSAL_REVIEW's own fields and PROPOSAL_REVISED's "current
 * revision" context.
 */ export function FieldTable({ proposed, existing, changedFields, workstreamText }) {
    const changed = new Set(changedFields);
    return /*#__PURE__*/ _jsx("table", {
        className: "w-full text-xs border rounded-md",
        children: /*#__PURE__*/ _jsxs("tbody", {
            children: [
                /*#__PURE__*/ _jsxs("tr", {
                    className: "border-b",
                    children: [
                        /*#__PURE__*/ _jsx("td", {
                            className: "px-2 py-1 font-medium text-gray-600 w-1/3",
                            children: "Workstream"
                        }),
                        /*#__PURE__*/ _jsx("td", {
                            className: "px-2 py-1 text-gray-900",
                            colSpan: 2,
                            children: workstreamText
                        })
                    ]
                }),
                FIELD_ROWS.map(({ key, label })=>{
                    const proposedValue = formatValue(proposed[key]);
                    const isChanged = existing !== undefined && changed.has(key);
                    if (isChanged) {
                        const existingValue = formatValue(existing?.[key]);
                        return /*#__PURE__*/ _jsxs("tr", {
                            className: "border-b last:border-b-0",
                            children: [
                                /*#__PURE__*/ _jsx("td", {
                                    className: "px-2 py-1 font-medium text-gray-600 align-top",
                                    children: label
                                }),
                                /*#__PURE__*/ _jsxs("td", {
                                    className: "px-2 py-1 text-gray-500 align-top",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-[10px] uppercase tracking-wide text-gray-400",
                                            children: "Existing"
                                        }),
                                        existingValue
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("td", {
                                    className: "px-2 py-1 text-gray-900 align-top",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-[10px] uppercase tracking-wide text-gray-400",
                                            children: "Proposed"
                                        }),
                                        proposedValue
                                    ]
                                })
                            ]
                        }, key);
                    }
                    return /*#__PURE__*/ _jsxs("tr", {
                        className: "border-b last:border-b-0",
                        children: [
                            /*#__PURE__*/ _jsx("td", {
                                className: "px-2 py-1 font-medium text-gray-600",
                                children: label
                            }),
                            /*#__PURE__*/ _jsx("td", {
                                className: "px-2 py-1 text-gray-900",
                                colSpan: 2,
                                children: proposedValue
                            })
                        ]
                    }, key);
                })
            ]
        })
    });
}
const OCCURRENCE_PREVIEW_COUNT = 6;
/**
 * A recurring proposal's full occurrence-date list, collapsed to the first
 * handful by default with a "Show all N dates" expander — the real workbook
 * has series running into the hundreds, so the full list must be reachable
 * but never dumped unbounded onto the page.
 */ export function OccurrenceList({ dates }) {
    const [showAll, setShowAll] = useState(false);
    if (dates.length === 0) return null;
    const visible = showAll ? dates : dates.slice(0, OCCURRENCE_PREVIEW_COUNT);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("ul", {
                "data-testid": Testids.deliverableImport.occurrenceList,
                className: "text-xs text-gray-700 max-h-40 overflow-auto border rounded-md divide-y",
                children: visible.map((date)=>/*#__PURE__*/ _jsx("li", {
                        className: "px-2 py-1",
                        children: date
                    }, date))
            }),
            dates.length > OCCURRENCE_PREVIEW_COUNT && /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": Testids.deliverableImport.occurrenceToggle,
                onClick: ()=>setShowAll((s)=>!s),
                className: "text-xs font-medium text-blue-600 hover:text-blue-700 mt-1",
                children: showAll ? 'Show fewer dates' : `Show all ${dates.length} dates`
            })
        ]
    });
}
/**
 * "What this will do": for a one-time deliverable, a plain sentence; for a
 * recurring cadence, an explicit statement that committing creates ONE
 * recurring task plus its full occurrence list.
 */ export function WhatThisWillDo({ fields }) {
    const dates = fields.occurrenceDates;
    if (dates.length > 1) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-1",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "text-xs text-gray-700",
                    children: [
                        "This creates ",
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium",
                            children: "one recurring task"
                        }),
                        " scheduled on",
                        ' ',
                        dates.length,
                        " occurrence",
                        dates.length === 1 ? '' : 's',
                        ":"
                    ]
                }),
                /*#__PURE__*/ _jsx(OccurrenceList, {
                    dates: dates
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "text-xs text-gray-700",
        children: [
            "This creates a single, one-time task",
            dates[0] ? ` due ${dates[0]}` : fields.firstDueDate ? ` due ${fields.firstDueDate}` : '',
            "."
        ]
    });
}
const DECISION_COPY = {
    [DeliverableImportDecision.APPROVED]: 'Approved — not committed yet',
    [DeliverableImportDecision.EDITED_APPROVED]: 'Approved — not committed yet',
    [DeliverableImportDecision.REJECTED]: 'Rejected',
    [DeliverableImportDecision.COMMITTED]: 'Committed to program tasks',
    [DeliverableImportDecision.COMMIT_FAILED]: 'Commit attempted and failed'
};
/**
 * Renders the server-truth decision state in place of live buttons —
 * APPROVED/EDITED_APPROVED get the "not committed yet" reassurance (nothing
 * writes to program data until commit), REJECTED shows its rationale, and
 * COMMITTED/COMMIT_FAILED show the commit outcome. This is what a page
 * reload shows, driven entirely by `proposal.decision` from the server.
 */ export function DecisionState({ proposal }) {
    const copy = DECISION_COPY[proposal.decision];
    if (!copy) return null;
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliverableImport.decisionState,
        className: "text-sm font-medium text-gray-800 border rounded-md px-2 py-1.5 bg-gray-50",
        children: [
            copy,
            proposal.decision === DeliverableImportDecision.REJECTED && /*#__PURE__*/ _jsx("div", {
                className: "text-xs font-normal text-gray-600 mt-1",
                children: proposal.decisionRationale || 'No reason given.'
            })
        ]
    });
}
/**
 * DEFER has no decision value in the proto — a deferred proposal stays
 * PENDING. This is local-only feedback for the click; it deliberately does
 * NOT persist across a reload, because the server has nothing to remember.
 */ export function DeferredNotice() {
    return /*#__PURE__*/ _jsx("div", {
        className: "text-xs font-medium text-gray-600",
        children: "Set aside for now — it will show up as pending again next time you load this review."
    });
}
/** Approved / Edited / Rejected / Pending counts for the proposal's workstream. */ export function WorkstreamProgressLine({ workstream }) {
    if (!workstream) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "text-xs text-gray-500",
        children: [
            workstreamLabel(workstream),
            ": ",
            workstream.approved,
            " approved, ",
            workstream.editedApproved,
            ' ',
            "edited, ",
            workstream.rejected,
            " rejected, ",
            workstream.pending,
            " pending"
        ]
    });
}
function toEditable(fields) {
    return {
        deliverableNumber: fields.deliverableNumber,
        title: fields.title,
        periodLabel: fields.periodLabel,
        firstDueDate: fields.firstDueDate,
        frequency: fields.frequency,
        pwsSections: fields.pwsSections.join(', '),
        responsiblePerson: fields.responsiblePerson
    };
}
/**
 * Merges edited canonical fields back over the full original DeliverableFields
 * so every field the human did NOT touch (clinNumber, periodOfPerformanceId,
 * seriesEndDate, occurrenceDates, dueDateNotes, ...) survives verbatim into
 * the revision payload — the proto requires the COMPLETE field set, not a
 * patch.
 */ function mergeEditable(original, edited) {
    return new DeliverableImportTypes.DeliverableFields({
        ...original,
        deliverableNumber: edited.deliverableNumber,
        title: edited.title,
        periodLabel: edited.periodLabel,
        firstDueDate: edited.firstDueDate,
        frequency: edited.frequency,
        pwsSections: edited.pwsSections.split(',').map((s)=>s.trim()).filter(Boolean),
        responsiblePerson: edited.responsiblePerson
    });
}
/**
 * Inline editor (not a modal) over the same canonical fields as the field
 * table. Save sends ReviseDeliverableImportProposal the COMPLETE
 * DeliverableFields — every untouched field is preserved from `original` via
 * mergeEditable, per the proto's "not a patch" warning. When the proposal was
 * NEEDS_CLARIFICATION, an action choice is required before saving so the
 * ambiguity is actually resolved.
 */ export function ProposalEditor({ original, needsClarification, onCancel, onSave }) {
    const [fields, setFields] = useState(()=>toEditable(original));
    const [reason, setReason] = useState('');
    const [resolvedAction, setResolvedAction] = useState('');
    const set = (key, value)=>setFields((f)=>({
                ...f,
                [key]: value
            }));
    const canSave = !needsClarification || resolvedAction !== '';
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-2 text-xs border rounded-md p-2 bg-gray-50",
        children: [
            needsClarification && /*#__PURE__*/ _jsxs("label", {
                className: "block",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-gray-700",
                        children: "Resolve as"
                    }),
                    /*#__PURE__*/ _jsxs("select", {
                        value: resolvedAction,
                        onChange: (e)=>setResolvedAction(Number(e.target.value)),
                        className: "mt-0.5 block w-full border rounded px-1.5 py-1",
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Choose…"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: DeliverableImportAction.CREATE,
                                children: "Create Deliverable"
                            }),
                            /*#__PURE__*/ _jsx("option", {
                                value: DeliverableImportAction.UPDATE,
                                children: "Update Deliverable"
                            })
                        ]
                    })
                ]
            }),
            [
                [
                    'deliverableNumber',
                    'Deliverable Number'
                ],
                [
                    'title',
                    'Title'
                ],
                [
                    'periodLabel',
                    'Period'
                ],
                [
                    'firstDueDate',
                    'Due Date'
                ],
                [
                    'frequency',
                    'Cadence'
                ],
                [
                    'pwsSections',
                    'PWS Section (comma-separated)'
                ],
                [
                    'responsiblePerson',
                    'Responsible Person'
                ]
            ].map(([key, label])=>/*#__PURE__*/ _jsxs("label", {
                    className: "block",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium text-gray-700",
                            children: label
                        }),
                        /*#__PURE__*/ _jsx("input", {
                            value: fields[key],
                            onChange: (e)=>set(key, e.target.value),
                            className: "mt-0.5 block w-full border rounded px-1.5 py-1"
                        })
                    ]
                }, key)),
            /*#__PURE__*/ _jsxs("label", {
                className: "block",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "font-medium text-gray-700",
                        children: "Reason for this change (optional)"
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        value: reason,
                        onChange: (e)=>setReason(e.target.value),
                        className: "mt-0.5 block w-full border rounded px-1.5 py-1"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 pt-1",
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.editSave,
                        disabled: !canSave,
                        onClick: ()=>onSave(mergeEditable(original, fields), reason, resolvedAction === '' ? undefined : resolvedAction),
                        className: "px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed",
                        children: "Save"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": Testids.deliverableImport.editCancel,
                        onClick: onCancel,
                        className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                        children: "Cancel"
                    })
                ]
            })
        ]
    });
}
/**
 * Resolve/Revise RPCs plus their shared side effects: invalidate every
 * GetDeliverableImportReview query for this change set so every card on
 * screen reflects the new server state, then send the
 * `[deliverable-import] event=...` feedback line the agent reads back.
 */ export function useProposalDecisionActions(payload) {
    const client = useDeliverableImportClient();
    const queryClient = useQueryClient();
    const sendFeedback = useChatFeedback();
    const afterMutate = async (event, workstreamKey, proposalId, text)=>{
        await invalidateDeliverableImportReview(queryClient, payload.programId, payload.changeSetId);
        sendFeedback(deliverableImportFeedback({
            event,
            changeSetId: payload.changeSetId,
            workstreamKey,
            proposalId,
            text
        }));
    };
    const resolve = useMutation({
        mutationFn: (vars)=>client.resolveDeliverableImportProposal({
                programId: payload.programId,
                changeSetId: payload.changeSetId,
                proposalId: vars.proposalId,
                verdict: vars.verdict,
                rationale: vars.rationale
            })
    });
    const revise = useMutation({
        mutationFn: (vars)=>client.reviseDeliverableImportProposal({
                programId: payload.programId,
                changeSetId: payload.changeSetId,
                proposalId: vars.proposalId,
                revised: vars.revised,
                reason: vars.reason,
                action: vars.action ?? DeliverableImportAction.UNSPECIFIED
            })
    });
    return {
        approve: (proposalId, workstreamKey)=>resolve.mutateAsync({
                proposalId,
                verdict: DeliverableImportVerdict.APPROVE,
                rationale: ''
            }).then(()=>afterMutate('approved', workstreamKey, proposalId, 'Program Lead approved this proposal.')),
        reject: (proposalId, workstreamKey, rationale)=>resolve.mutateAsync({
                proposalId,
                verdict: DeliverableImportVerdict.REJECT,
                rationale
            }).then(()=>afterMutate('rejected', workstreamKey, proposalId, rationale ? `Program Lead rejected this proposal: ${rationale}` : 'Program Lead rejected this proposal.')),
        defer: (proposalId, workstreamKey)=>resolve.mutateAsync({
                proposalId,
                verdict: DeliverableImportVerdict.DEFER,
                rationale: ''
            }).then(()=>afterMutate('deferred', workstreamKey, proposalId, 'Program Lead set this proposal aside for now.')),
        save: (proposalId, workstreamKey, revised, reason, action)=>revise.mutateAsync({
                proposalId,
                revised,
                reason,
                action
            }).then(()=>afterMutate('revised', workstreamKey, proposalId, reason ? `Program Lead revised this proposal: ${reason}` : 'Program Lead revised this proposal.')),
        isSubmitting: resolve.isPending || revise.isPending,
        error: resolve.error ?? revise.error
    };
}
/**
 * PENDING-state action bar shared by PROPOSAL_REVIEW and PROPOSAL_REVISED —
 * only the approve/edit copy and testids differ between them (plain
 * "Approve"/"Edit" vs "Approve Revised Change"/"Edit Again"). NEEDS_CLARIFICATION
 * proposals never get a bare Approve — the proto requires those to be
 * resolved via revise first — so this renders only Edit / Reject / Decide
 * Later for that action, with a sentence explaining why.
 */ export function ProposalActionBar({ needsClarification, approveLabel, approveTestid, editLabel, editTestid, onApprove, onEdit, onReject, onDecideLater, disabled }) {
    const [rejecting, setRejecting] = useState(false);
    const [reason, setReason] = useState('');
    if (rejecting) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsx("input", {
                    "data-testid": Testids.deliverableImport.rejectReason,
                    value: reason,
                    onChange: (e)=>setReason(e.target.value),
                    placeholder: "Reason (optional)",
                    className: "block w-full text-xs border rounded px-1.5 py-1"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "data-testid": Testids.deliverableImport.rejectConfirm,
                            disabled: disabled,
                            onClick: ()=>onReject(reason),
                            className: "px-2.5 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50",
                            children: "Confirm Reject"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>setRejecting(false),
                            className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                            children: "Cancel"
                        })
                    ]
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        children: [
            !needsClarification && /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": approveTestid,
                disabled: disabled,
                onClick: onApprove,
                className: "px-2.5 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50",
                children: approveLabel
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": editTestid,
                disabled: disabled,
                onClick: onEdit,
                className: "px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50",
                children: editLabel
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": Testids.deliverableImport.reject,
                disabled: disabled,
                onClick: ()=>setRejecting(true),
                className: "text-xs font-medium text-red-700 hover:text-red-800",
                children: "Reject"
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": Testids.deliverableImport.decideLater,
                disabled: disabled,
                onClick: onDecideLater,
                className: "text-xs font-medium text-gray-600 hover:text-gray-800",
                children: "Decide Later"
            })
        ]
    });
}
